import React, { useState, useRef, useEffect } from 'react';

const PRESETS = [
  { label: 'Today',        getValue: () => { const d = new Date().toISOString().split('T')[0]; return { start: d, end: d }; } },
  { label: 'Yesterday',    getValue: () => { const d = new Date(); d.setDate(d.getDate()-1); const s = d.toISOString().split('T')[0]; return { start: s, end: s }; } },
  { label: 'Last 7 days',  getValue: () => { const e = new Date(); const s = new Date(); s.setDate(s.getDate()-6); return { start: s.toISOString().split('T')[0], end: e.toISOString().split('T')[0] }; } },
  { label: 'Last 30 days', getValue: () => { const e = new Date(); const s = new Date(); s.setDate(s.getDate()-29); return { start: s.toISOString().split('T')[0], end: e.toISOString().split('T')[0] }; } },
  { label: 'This month',   getValue: () => { const n = new Date(); return { start: new Date(n.getFullYear(),n.getMonth(),1).toISOString().split('T')[0], end: n.toISOString().split('T')[0] }; } },
  { label: 'Last month',   getValue: () => { const n = new Date(); const s = new Date(n.getFullYear(),n.getMonth()-1,1); const e = new Date(n.getFullYear(),n.getMonth(),0); return { start: s.toISOString().split('T')[0], end: e.toISOString().split('T')[0] }; } },
  { label: 'Year to date', getValue: () => { const n = new Date(); return { start: new Date(n.getFullYear(),0,1).toISOString().split('T')[0], end: n.toISOString().split('T')[0] }; } },
  { label: 'Last year',    getValue: () => { const n = new Date(); const y = n.getFullYear()-1; return { start: `${y}-01-01`, end: `${y}-12-31` }; } },
];

function formatLabel(start, end) {
  if (!start && !end) return 'All time';
  const fmt = d => d ? new Date(d + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '';
  if (start === end) return fmt(start);
  return `${fmt(start)} – ${fmt(end)}`;
}

// ── Reusable filter bar for Leads and Quotes pages ──
export default function FilterBar({
  // date
  startDate, endDate, onDateChange,
  // contact search
  contact, onContactChange,
  // type filter
  typeOptions, selectedType, onTypeChange, typePlaceholder,
  // amount range
  minAmount, maxAmount, onAmountChange,
  // active filter count + reset
  onReset,
}) {
  const [dateOpen, setDateOpen] = useState(false);
  const [localStart, setLocalStart] = useState(startDate || '');
  const [localEnd, setLocalEnd] = useState(endDate || '');
  const dateRef = useRef(null);

  useEffect(() => {
    function outside(e) { if (dateRef.current && !dateRef.current.contains(e.target)) setDateOpen(false); }
    document.addEventListener('mousedown', outside);
    return () => document.removeEventListener('mousedown', outside);
  }, []);

  const applyPreset = (preset) => {
    const { start, end } = preset.getValue();
    setLocalStart(start); setLocalEnd(end);
    onDateChange(start, end);
    setDateOpen(false);
  };

  const applyCustom = () => {
    if (localStart && localEnd) { onDateChange(localStart, localEnd); setDateOpen(false); }
  };

  const clearDates = () => { setLocalStart(''); setLocalEnd(''); onDateChange('', ''); setDateOpen(false); };

  // Count active filters
  const activeCount = [
    startDate || endDate,
    contact,
    selectedType,
    minAmount || maxAmount,
  ].filter(Boolean).length;

  return (
    <div style={{ background: 'white', border: '1px solid #DDE7F1', borderRadius: '10px', padding: '14px 16px', marginBottom: '16px', boxShadow: '0 1px 3px rgba(15,23,42,0.05)' }}>
      <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'flex-end' }}>

        {/* ── Date range ── */}
        <div style={{ flex: '1 1 200px', minWidth: '180px' }} ref={dateRef}>
          <div style={{ fontSize: '11px', fontWeight: '600', color: '#64748B', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '5px' }}>Date Range</div>
          <div style={{ position: 'relative', zIndex: 50 }}>
            <button
              onClick={() => setDateOpen(!dateOpen)}
              style={{
                width: '100%', height: '36px', padding: '0 10px 0 32px',
                border: `1px solid ${(startDate || endDate) ? '#2D87F0' : '#DDE7F1'}`,
                borderRadius: '8px', background: (startDate || endDate) ? '#EFF6FF' : 'white',
                fontSize: '13px', color: (startDate || endDate) ? '#2D87F0' : '#334155',
                fontWeight: (startDate || endDate) ? '600' : '400',
                cursor: 'pointer', textAlign: 'left', display: 'flex', alignItems: 'center',
              }}
            >
              <i className="fas fa-calendar-alt" style={{ position: 'absolute', left: '10px', color: (startDate || endDate) ? '#2D87F0' : '#94A3B8', fontSize: '12px' }}></i>
              {formatLabel(startDate, endDate)}
            </button>

            {dateOpen && (
              <div style={{ position: 'absolute', top: 'calc(100% + 4px)', left: 0, background: 'white', border: '1px solid #DDE7F1', borderRadius: '10px', boxShadow: '0 8px 24px rgba(15,23,42,0.12)', zIndex: 200, minWidth: '380px', padding: '12px' }}>
                <div style={{ display: 'flex', gap: '12px' }}>
                  {/* Presets */}
                  <div style={{ width: '140px', borderRight: '1px solid #DDE7F1', paddingRight: '12px' }}>
                    <div style={{ fontSize: '10px', fontWeight: '700', color: '#94A3B8', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '6px' }}>Quick Select</div>
                    {PRESETS.map(p => (
                      <button key={p.label} onClick={() => applyPreset(p)}
                        style={{ display: 'block', width: '100%', textAlign: 'left', padding: '6px 8px', fontSize: '13px', borderRadius: '5px', border: 'none', background: 'none', cursor: 'pointer', color: '#334155' }}
                        onMouseEnter={e => e.target.style.background = '#F8FAFC'}
                        onMouseLeave={e => e.target.style.background = 'none'}>
                        {p.label}
                      </button>
                    ))}
                  </div>
                  {/* Custom */}
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: '10px', fontWeight: '700', color: '#94A3B8', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '6px' }}>Custom Range</div>
                    <div style={{ marginBottom: '8px' }}>
                      <label style={{ fontSize: '12px', color: '#64748B', display: 'block', marginBottom: '3px' }}>Start</label>
                      <input type="date" className="input-field" value={localStart} onChange={e => setLocalStart(e.target.value)} style={{ height: '32px', fontSize: '12px' }} />
                    </div>
                    <div style={{ marginBottom: '10px' }}>
                      <label style={{ fontSize: '12px', color: '#64748B', display: 'block', marginBottom: '3px' }}>End</label>
                      <input type="date" className="input-field" value={localEnd} onChange={e => setLocalEnd(e.target.value)} style={{ height: '32px', fontSize: '12px' }} />
                    </div>
                    <div style={{ display: 'flex', gap: '6px' }}>
                      <button onClick={clearDates} className="btn-secondary" style={{ flex: 1, height: '30px', fontSize: '12px', padding: 0 }}>Clear</button>
                      <button onClick={applyCustom} className="btn-primary" style={{ flex: 1, height: '30px', fontSize: '12px', padding: 0 }} disabled={!localStart || !localEnd}>Apply</button>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* ── Contact search ── */}
        <div style={{ flex: '1 1 180px', minWidth: '160px' }}>
          <div style={{ fontSize: '11px', fontWeight: '600', color: '#64748B', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '5px' }}>Contact</div>
          <div style={{ position: 'relative' }}>
            <i className="fas fa-user" style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', color: '#94A3B8', fontSize: '12px', pointerEvents: 'none' }}></i>
            <input
              type="text" className="input-field"
              style={{ height: '36px', paddingLeft: '30px', fontSize: '13px', border: contact ? '1px solid #2D87F0' : '1px solid #DDE7F1', background: contact ? '#EFF6FF' : 'white', color: contact ? '#2D87F0' : undefined }}
              placeholder="Name or email..."
              value={contact}
              onChange={e => onContactChange(e.target.value)}
            />
          </div>
        </div>

        {/* ── Type filter ── */}
        {typeOptions && (
          <div style={{ flex: '1 1 160px', minWidth: '140px' }}>
            <div style={{ fontSize: '11px', fontWeight: '600', color: '#64748B', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '5px' }}>Type</div>
            <div style={{ position: 'relative' }}>
              <select
                className="input-field"
                value={selectedType}
                onChange={e => onTypeChange(e.target.value)}
                style={{ height: '36px', fontSize: '13px', appearance: 'none', cursor: 'pointer', paddingRight: '28px', border: selectedType ? '1px solid #2D87F0' : '1px solid #DDE7F1', background: selectedType ? '#EFF6FF' : 'white', color: selectedType ? '#2D87F0' : undefined, fontWeight: selectedType ? '600' : '400' }}
              >
                <option value="">{typePlaceholder || 'All types'}</option>
                {typeOptions.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
              <i className="fas fa-chevron-down" style={{ position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)', color: selectedType ? '#2D87F0' : '#94A3B8', fontSize: '11px', pointerEvents: 'none' }}></i>
            </div>
          </div>
        )}

        {/* ── Amount range ── */}
        {onAmountChange && (
          <div style={{ flex: '1 1 200px', minWidth: '180px' }}>
            <div style={{ fontSize: '11px', fontWeight: '600', color: '#64748B', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '5px' }}>Coverage Amount</div>
            <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
              <div style={{ position: 'relative', flex: 1 }}>
                <span style={{ position: 'absolute', left: '8px', top: '50%', transform: 'translateY(-50%)', color: '#94A3B8', fontSize: '12px', pointerEvents: 'none' }}>$</span>
                <input type="number" className="input-field"
                  style={{ height: '36px', paddingLeft: '18px', fontSize: '13px', border: minAmount ? '1px solid #2D87F0' : '1px solid #DDE7F1' }}
                  placeholder="Min" value={minAmount}
                  onChange={e => onAmountChange(e.target.value, maxAmount)} />
              </div>
              <span style={{ color: '#94A3B8', fontSize: '12px', flexShrink: 0 }}>–</span>
              <div style={{ position: 'relative', flex: 1 }}>
                <span style={{ position: 'absolute', left: '8px', top: '50%', transform: 'translateY(-50%)', color: '#94A3B8', fontSize: '12px', pointerEvents: 'none' }}>$</span>
                <input type="number" className="input-field"
                  style={{ height: '36px', paddingLeft: '18px', fontSize: '13px', border: maxAmount ? '1px solid #2D87F0' : '1px solid #DDE7F1' }}
                  placeholder="Max" value={maxAmount}
                  onChange={e => onAmountChange(minAmount, e.target.value)} />
              </div>
            </div>
          </div>
        )}

        {/* ── Reset ── */}
        {activeCount > 0 && (
          <div style={{ display: 'flex', alignItems: 'flex-end', paddingBottom: '0' }}>
            <button onClick={onReset}
              style={{ height: '36px', padding: '0 14px', background: 'none', border: '1px solid #DDE7F1', borderRadius: '8px', fontSize: '13px', color: '#64748B', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px', whiteSpace: 'nowrap' }}>
              <i className="fas fa-times" style={{ fontSize: '11px' }}></i>
              Clear filters
              <span style={{ background: '#2D87F0', color: 'white', borderRadius: '999px', padding: '1px 6px', fontSize: '11px', fontWeight: '700' }}>{activeCount}</span>
            </button>
          </div>
        )}

      </div>
    </div>
  );
}
